import { DefaultError } from '@tanstack/query-core'
import {
  useQueries,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import axios from 'axios'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { CreditCardStatus } from 'src/models/creditCard.models'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { statisticsQueryKeys } from 'src/queries/queries.constants'
import {
  StatisticsKeys,
  StatisticsRequestsBody,
} from 'src/queries/statistics/statistics.queries.constants'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import {
  CreditCardStatisticRequest,
  StatisticAggregateType,
  StatisticModelType,
  StatisticsRequest,
} from 'src/requests/statistics.request'

interface StatisticsResponse {
  statistics: Array<{ value: number }>
}

type StatisticsMappedResponse<T extends readonly StatisticsKeys[]> = {
  [K in T[number]]: number | undefined
}

interface UseQueryStatisticsOptions<T extends readonly StatisticsKeys[]>
  extends Omit<UseQueryOptions, 'queryKey'> {
  statisticsKeys: T
}

type UseStatisticQueryOptions = Array<UseQueryOptions<StatisticsResponse>>
type StatisticsMappedResults<T extends readonly StatisticsKeys[]> = {
  [K in T[number]]: UseQueryResult<number | undefined>
}
type UseStatisticsQueryResult<T extends readonly StatisticsKeys[]> = {
  results: StatisticsMappedResults<T>
  anyLoading: boolean
}

// TODO: the only use case for a useQuery request that's actually a POST, has to manually implement useQuery directly
/**
 * Fetches statistics for a list of statistic keys as separate queries with their own independent results.
 * If multiple statistics are needed, but they don't all need to be loaded at the same time, or it is more efficient
 * to query them as separate requests, this hook can be used.
 */
export const useQueryStatistics = <T extends readonly StatisticsKeys[]>(
  options: UseQueryStatisticsOptions<T>,
): UseStatisticsQueryResult<T> => {
  const requestConfig = useSelector(selectAuthedJsonApiRequestConfig)

  const combineStatistics = useCallback(
    (
      results: Array<UseQueryResult<StatisticsResponse>>,
    ): UseStatisticsQueryResult<T> => {
      const mappedResults = results.reduce((acc, result, i) => {
        return {
          ...acc,
          [options.statisticsKeys[i]]: {
            ...result,
            data: result.data?.statistics[0].value,
          },
        }
      }, {} as StatisticsMappedResults<T>)

      return {
        results: mappedResults,
        anyLoading: results.some((result) => result.isLoading),
      }
    },
    [options.statisticsKeys],
  )

  return useQueries<UseStatisticQueryOptions, UseStatisticsQueryResult<T>>({
    queries: options.statisticsKeys.map((statisticKey) => ({
      queryKey: statisticsQueryKeys.list([statisticKey]),
      queryFn: async () => {
        return await axios
          .post<StatisticsResponse>(
            '/statistics',
            { statistics: [StatisticsRequestsBody[statisticKey]] },
            requestConfig,
          )
          .then((response) => response.data)
      },
      ...(options.enabled === false ? { enabled: false as const } : {}),
    })),
    combine: combineStatistics,
  })
}

/**
 * Query key for the onboarding drop-off force-register check (EX-40009).
 *
 * It extends the shared statistics key for `CreditCardsOrgSourceCount` with the
 * per-page-visit `cacheToken`, so React Query treats every visit as a distinct
 * fetch (rather than serving its own cached/deduped value) and the busted
 * request — carrying `query.cacheTimestamp` — actually reaches the network.
 * Exported so tests can assert the key varies with the token.
 */
export const onboardingDropoffSourceCountQueryKey = (cacheToken: string) =>
  [
    ...statisticsQueryKeys.list([StatisticsKeys.CreditCardsOrgSourceCount]),
    cacheToken,
  ] as const

/**
 * EX-40009 — focused query for the onboarding drop-off force-register check.
 *
 * The org source-card count is served from a stale extend-api Redis cache (key
 * derived from the request's `toString()`), so a freshly-onboarded owner can
 * read `0` for up to the TTL and get bounced back to `/onboarding/card`. The
 * shared `useQueryStatistics` can't fix this: its query key is FIXED per stat,
 * so a body-only `cacheTimestamp` change would be served from the React Query
 * cache and never refetch (the iOS author's explicit web note — see PR #2926).
 *
 * This query folds the per-visit `cacheToken` into BOTH layers:
 *   - the query KEY, so React Query actually sends the request each visit; and
 *   - the request body's `query.cacheTimestamp`, so the backend Redis cache
 *     misses and recomputes the count live from the database.
 *
 * Only `CreditCardsOrgSourceCount` is busted — the shared
 * `StatisticsRequestsBody` constant is left untouched, so every other stat
 * keeps the shared cached path (surgical busting, mirroring extend-ios #2926).
 * Returns a `UseQueryResult<number | undefined>` (the count) so the mapping in
 * `useOnboardingDropoffStatus` stays a thin destructure onto the existing
 * `shouldRedirect` / `isResumingOnboarding` decision logic.
 */
export const useQueryOnboardingDropoffSourceCount = ({
  cacheToken,
  enabled,
}: {
  cacheToken: string
  enabled: boolean
}): UseQueryResult<number | undefined> => {
  const requestConfig = useSelector(selectAuthedJsonApiRequestConfig)

  return useQuery<StatisticsResponse, DefaultError, number | undefined>({
    queryKey: onboardingDropoffSourceCountQueryKey(cacheToken),
    queryFn: async () => {
      // Copy the shared stat definition and stamp the busting token onto its
      // query — never mutate the shared `StatisticsRequestsBody` constant. The
      // `as` narrows the `Record<…, StatisticRequest>` union lookup to the
      // credit-card variant (the only one whose query carries `cacheTimestamp`).
      const base = StatisticsRequestsBody[
        StatisticsKeys.CreditCardsOrgSourceCount
      ] as CreditCardStatisticRequest
      const bustedStatistic: CreditCardStatisticRequest = {
        ...base,
        query: { ...base.query, cacheTimestamp: cacheToken },
      }
      return await axios
        .post<StatisticsResponse>(
          '/statistics',
          { statistics: [bustedStatistic] },
          requestConfig,
        )
        .then((response) => response.data)
    },
    select: (data) => data?.statistics[0]?.value,
    enabled,
  })
}

// TODO: the only use case for a useQuery request that's actually a POST, has to manually implement useQuery directly
// TODO: how can we better ensure we aren't over-fetching statistics when there is overlap between the lists of keys?
/**
 * Fetches statistics for a list of statistic keys in a single request, relying on the backend to parallelize them.
 * The whole requests data will only be available once all statistics are fetched.
 */

// eslint-disable-next-line no-unused-vars
const useQueryBatchedStatistics = <T extends StatisticsKeys[]>(
  options: UseQueryStatisticsOptions<T>,
) => {
  const requestConfig = useSelector(selectAuthedJsonApiRequestConfig)

  const statisticsRequest: StatisticsRequest = useMemo(
    () => ({
      statistics: options.statisticsKeys.map((statisticName) => ({
        ...StatisticsRequestsBody[statisticName],
      })),
    }),
    [options.statisticsKeys],
  )

  const selectStatistics = useCallback(
    (data: StatisticsResponse) => {
      return data?.statistics.reduce((acc, { value }, i) => {
        return { ...acc, [options.statisticsKeys[i]]: value }
      }, {} as StatisticsMappedResponse<T>)
    },
    [options.statisticsKeys],
  )

  return useQuery<
    StatisticsResponse,
    DefaultError,
    StatisticsMappedResponse<T>
  >({
    queryKey: statisticsQueryKeys.list(options.statisticsKeys),
    queryFn: async () => {
      return await axios
        .post<StatisticsResponse>(
          '/statistics',
          statisticsRequest,
          requestConfig,
        )
        .then((response) => response.data)
    },
    select: selectStatistics,
  })
}

interface CreditCardStatisticsResponse {
  activeBudgetCount: number
  creditCardIds: string[]
  statistics: Array<{ value: number }>
}

export const useQueryCreditCardVCNStatistics = (
  creditCardIds: string[],
  type: StatisticModelType,
  enabled: boolean,
) => {
  const requestConfig = useSelector(selectAuthedJsonApiRequestConfig)
  return useQuery({
    queryKey: ['creditCardStatistics', 'vcn'],
    queryFn: async () => {
      const response = await axios.post<CreditCardStatisticsResponse>(
        `/statistics`,
        {
          statistics: creditCardIds.map((ccId) => ({
            agg: StatisticAggregateType.Count,
            type: type,
            query: {
              status: VirtualCardStatus.Active,
              creditCardId: ccId,
              all: true,
            },
          })),
        },
        requestConfig,
      )
      return {
        ...response.data,
        creditCardIds,
        statistics: response.data.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [creditCardIds[index]]: curr.value,
          }),
          {} as Record<string, number>,
        ),
      }
    },
    enabled,
  })
}

export const useQueryCreditCardBudgetsStatistics = (
  creditCardIds: string[],
  type: StatisticModelType,
  enabled: boolean,
) => {
  const requestConfig = useSelector(selectAuthedJsonApiRequestConfig)
  return useQuery({
    queryKey: ['creditCardStatistics', 'budgets'],
    queryFn: async () => {
      const response = await axios.post<CreditCardStatisticsResponse>(
        `/statistics`,
        {
          statistics: creditCardIds.map((ccId) => ({
            agg: StatisticAggregateType.Count,
            type: type,
            query: {
              status: CreditCardStatus.Active,
              parentCreditCard: ccId,
              all: true,
            },
          })),
        },
        requestConfig,
      )
      return {
        ...response.data,
        creditCardIds,
        statistics: response.data.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [creditCardIds[index]]: curr.value,
          }),
          {} as Record<string, number>,
        ),
      }
    },
    enabled,
  })
}
